import * as React from 'react';
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  ITextStyles,
  Link,
  MessageBar,
  MessageBarType,
  Stack,
  Text
} from '@fluentui/react';
import { QueueConfiguration, postUpsertQueueConfig } from '../../services';
import { useHistory } from 'react-router';
import { SettingsLeavePromptMessage } from '../navigation';
import { SalusPrimaryButton } from '../shared/SalusPrimaryButton';

interface CreateQueueTitleProps {
  tenantId: string;
  tenantDisplayName: string;
  goToPreviousTab: () => void;
  goToNextTab: () => void;
  currentTab: number;
  currentFormComplete: boolean;
  newQueue?: QueueConfiguration;
}

const displayNamesStyle: ITextStyles = {
  root: {
    height: 'auto',
    lineHeight: 36,
    marginTop: 15
  }
};

export const CreateQueueTitle: React.FunctionComponent<CreateQueueTitleProps> = (props: CreateQueueTitleProps) => {
  const [serviceError, setServiceError] = React.useState<boolean>(false);
  const [showPrompt, setShowPrompt] = React.useState<boolean>(false);
  const [started, setStarted] = React.useState<boolean>(false);

  const history = useHistory();

  const onReturnToDashboard = () => {
    if (started) {
      setShowPrompt(true);
    } else {
      history.push(`/dashboard?tab=${props.tenantId}`);
    }
  };

  async function onSaveButtonClick(): Promise<void> {
    // Making GMC call to upsert configuration
    if (props.newQueue) {
      try {
        setServiceError(false);
        await postUpsertQueueConfig(props.newQueue);
      } catch (exception) {
        setServiceError(true);
        return;
      }
      history.push(
        `/settings/tenantId/${props.newQueue.tenant.tenantId}/titleId/${props.newQueue.title.titleId}/subjectType/${props.newQueue.subjectType}?tab=access&isNewQueue=true`
      );
    }
  }

  return (
    <Stack style={{ marginBottom: 16 }}>
      <Link style={{ marginLeft: 55, marginTop: 16 }} onClick={onReturnToDashboard}>
        {`< ${props.tenantDisplayName} Dashboard `}
      </Link>
      <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: 50 }} style={{ marginLeft: 55 }}>
        <Stack>
          <Text variant={'xxLargePlus'} styles={displayNamesStyle}>
            {'Create a queue'}
          </Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }} styles={{ root: { marginRight: 94, marginTop: 12 } }}>
          <DefaultButton
            onClick={props.goToPreviousTab}
            disabled={props.currentTab <= 1}
            style={{ width: 120, height: 40 }}
            text="Back"
          />
          {props.currentTab >= 3 ? (
            <SalusPrimaryButton
              text="Create queue"
              disabled={!props.currentFormComplete}
              onClick={onSaveButtonClick}
              style={{ width: 160, height: 40 }}
            />
          ) : (
            <SalusPrimaryButton
              onClick={() => {
                setStarted(true);
                props.goToNextTab();
              }}
              disabled={props.currentTab >= 3 || !props.currentFormComplete}
              style={{ width: 120, height: 40 }}
              text="Next"
            />
          )}
        </Stack>
      </Stack>
      {serviceError && (
        <Stack style={{ marginLeft: '-1rem' }}>
          <Stack.Item>
            <MessageBar
              messageBarType={MessageBarType.error}
              isMultiline={false}
              dismissButtonAriaLabel="Close"
              styles={{ iconContainer: { marginLeft: 73 } }}
            >
              There was an error while attempting to create the configuration, please try again.
            </MessageBar>
          </Stack.Item>
        </Stack>
      )}
      <Dialog
        hidden={!showPrompt}
        dialogContentProps={{
          title: 'Exit to Dashboard?',
          subText: SettingsLeavePromptMessage
        }}
        modalProps={{
          overlay: {
            isDarkThemed: true
          }
        }}
      >
        <DialogFooter>
          <SalusPrimaryButton onClick={() => history.push(`/dashboard?tab=${props.tenantId}`)} text="Yes" />
          <DefaultButton onClick={() => setShowPrompt(false)} text="No" />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};
